import React, { useState, useRef, useEffect, forwardRef } from "react";
import { useDataContext } from "App";
import { TextField, IconButton, ClickAwayListener, Popper, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import styles from "header/styles.module.scss";
import FastEntryStyleResults from "./FastEntryStyleResults";
import useSearchConfig from "useSearchConfig";
import { resetGlobalFocusFlow } from './hooks/useFocusFlow';

const FastEntryStyleSearchBar = forwardRef(({
  id,
  onStyleSelect,
  onStyleEdit,
  setAvailableColors,
  item,
  selectedStyle,
  setSelectedColor,
  initialSelectedColor,
  onColorSelect,
  defaultValue = "",
  onUpdate,
  onMount
}, ref) => {
  const [inputValue, setInputValue] = useState("");
  const [autoCompleteSuggestions, setAutoCompleteSuggestions] = useState([]);
  const [availableColorsLocal, setAvailableColorsLocal] = useState([]);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const { performSearch } = useSearchConfig();
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  const [popperOpen, setPopperOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  
  const initialSelectionRef = useRef(false);
  const initialColorSelectionRef = useRef(false);
  const [methods, setMethods] = useState(null);

  // Get methods from parent
  useEffect(() => {
    if (onMount && typeof onMount === 'function') {
      const parentMethods = onMount({});
      setMethods(parentMethods);
    }
  }, [onMount]);

  const autoRecs = item?.alternatives?.map((alt) => {
    return {
      dgi_style_sku: alt.dgi_style_sku,
      master_style: alt.style_number,
      master_short_description: alt.master_short_description,
      colors: alt.colors?.map(color => color.name)
    };
  }) || [];

  const submitQuery = async (value = inputValue) => {
    const searchResp = await performSearch(value);
    const hits = searchResp?.results?.[0]?.hits;
    setAutoCompleteSuggestions(hits?.map(hit => ({
      ...hit,
      colors: hit.colors?.map(c => c.name)
    })));
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && 
        !event.isDefaultPrevented() &&
        autoCompleteSuggestions?.[0]) {
      event.preventDefault();
      onStyleSelect(autoCompleteSuggestions[0]);
    }
  };

  const hasResults = 
    (inputValue === "" && autoRecs.length > 0) ||
    (inputValue !== "" && autoCompleteSuggestions.length > 0);
  
  const hasAutoSearchResults = 
    inputValue === "" && autoCompleteSuggestions.length > 0;

  const handleStyleSelect = (style) => {
    setInputValue(`${style.master_style} - ${style.master_brand}`);
    
    const colors = style?.colors || [];
    setAvailableColorsLocal(colors);
    
    if (typeof setAvailableColors === 'function') {
      setAvailableColors(colors);
    }
    
    setPopperOpen(false);
    setIsFocused(false);
    
    // Turn off edit mode when a style is selected
    if (methods && methods.setEditMode) {
      methods.setEditMode(false);
    }
    
    onStyleSelect(style);
    setShowSuggestions(false);
    
    if (onUpdate) {
      onUpdate({
        style: style.master_style,
        brand: style.master_brand,
        description: style.master_short_description,
        dgi_style_sku: style.dgi_style_sku,
      });
    }
  };

  useEffect(() => {
    if (item?.style_number) {
      submitQuery(item.style_number);
    }
  }, [item]);

  useEffect(() => {
    if (availableColorsLocal.length > 0 && initialSelectedColor && !initialColorSelectionRef.current) {
      const colorToSelect = availableColorsLocal.find(color => color === initialSelectedColor);
      
      if (colorToSelect && onColorSelect) {
        initialColorSelectionRef.current = true;
        
        if (setSelectedColor) {
          setSelectedColor(null);
        }
      }
    }
  }, [availableColorsLocal, initialSelectedColor]);

  useEffect(() => {
    submitQuery("");
  }, []);

  useEffect(() => {
    if (selectedStyle?.master_style) {
      setInputValue(`${selectedStyle.master_style} - ${selectedStyle.master_brand}`);
      
      if (autoCompleteSuggestions.length === 0) {
        submitQuery(selectedStyle.master_style);
      }
      
      const matchingStyle = autoCompleteSuggestions.find(
        suggestion => suggestion.master_style === selectedStyle.master_style && 
                     suggestion.master_brand === selectedStyle.master_brand
      );
      
      if (matchingStyle && matchingStyle.dgi_style_sku) {
        handleStyleSelect(matchingStyle);
      }
    }
  }, [selectedStyle, autoCompleteSuggestions]);

  const handleFocus = (e) => {
    setIsFocused(true);
    if (e.target) {
      setTimeout(() => {
        e.target.select();
      }, 0);
    }
    if (e.isTrusted) {
      setPopperOpen(true);
    }
  };

  const handleClickAway = () => {
    setPopperOpen(false);
    setIsFocused(false);
  };

  const handleInputChange = (e) => {
    // Remove special characters from input
    const sanitizedValue = e.target.value.replace(/[^\w\s-]/g, '');

    // Reset the global focus flow state to prevent automatic focus movement
    resetGlobalFocusFlow();
    
    // Set edit mode when typing in the input
    if (methods && methods.setEditMode && selectedStyle) {
      methods.setEditMode(true);
    }
    
    // If this is the first character typed after selecting a style, clear the input
    if (selectedStyle && e.target.value !== inputValue && 
        (inputValue.includes(selectedStyle.master_style) || inputValue.includes(selectedStyle.master_brand))) {
      // This is the first character typed - replace the entire input with just this character
      setInputValue(sanitizedValue.slice(-1));
      
      // Also notify parent that we're editing the style
      if (onStyleEdit) {
        onStyleEdit();
      }
      
      // Clear available colors
      setAvailableColorsLocal([]);
      if (typeof setAvailableColors === 'function') {
        setAvailableColors([]);
      }
    } else {
      // Normal typing - just update the input value
      setInputValue(sanitizedValue);
    }
    
    setShowSuggestions(true);
    submitQuery(sanitizedValue);
    
    if (e.nativeEvent.inputType === "deleteContentBackward") {
      if (onStyleEdit) {
        onStyleEdit();
      }
      
      setAvailableColorsLocal([]);
      if (typeof setAvailableColors === 'function') {
        setAvailableColors([]);
      }
    }
  };

  const handleInputClick = () => {
    if (selectedStyle) {
      // Set edit mode when clicking into the input
      if (methods && methods.setEditMode) {
        methods.setEditMode(true);
      }
      
      // Reset the global focus flow state
      resetGlobalFocusFlow();
      
      // Call handleFocus to open the popper
      handleFocus({ isTrusted: true });
    }
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <div style={{ position: 'relative' }} id={id}>
        <div
          ref={containerRef}
          className={`
            w-full bg-white 
            h-9 
            rounded-tr-[20px] rounded-tl-[20px]
            ${!popperOpen ? "rounded-br-[20px] rounded-bl-[20px]" : ""}
            border border-solid border-[rgba(0,0,0,0.12)]
            ${popperOpen ? "border-b-0" : ""}`}
          style={{ position: 'relative', zIndex: 1000 }}
        >
          <div className="flex flex-row items-end gap-[0.5rem] w-full h-full p-[2px]">
            <div className="flex-grow h-full w-full relative">
              <TextField
                fullWidth
                inputRef={inputRef}
                autoComplete="off"
                value={inputValue}
                onChange={handleInputChange}
                onClick={handleInputClick}
                onFocus={handleFocus}
                placeholder="Enter style number or brand..."
                variant="standard"
                className="pb-[1px]"
                InputProps={{
                  disableUnderline: true,
                  className:
                    "custom-search-input bg-white w-full min-h-full rounded-l-full rounded-r-full flex justify-start pt-[0px]",
                  classes: {
                    underline: "custom-underline",
                  },
                  startAdornment: (
                    <IconButton
                      onClick={() => submitQuery()}
                      className="hover:bg-transparent"
                      aria-label="search"
                      size="small"
                      style={{ 
                        marginLeft: '4px',
                        marginTop: '2px'
                      }}
                    >
                      <SearchIcon className="fill-charcoal text-[16px]" />
                    </IconButton>
                  ),
                  inputProps: {
                    className: `${styles.customPlaceholder} ${
                      inputValue.length > 0 && "pt-[6px]"
                    }`,
                    style: { 
                      paddingLeft: '4px',
                      fontSize: '0.9rem'
                    }
                  },
                }}
                onKeyDown={handleKeyDown}
              />
            </div>
          </div>
        </div>

        <Popper
          open={popperOpen}
          anchorEl={containerRef.current}
          placement="bottom-start"
          style={{ width: containerRef.current?.offsetWidth || 'auto', zIndex: 1001 }}
          modifiers={[
            {
              name: 'preventOverflow',
              enabled: false,
            },
            {
              name: 'flip',
              enabled: false,
            }
          ]}
        >
          <div 
            className="border border-solid border-[rgba(0,0,0,0.12)] bg-white"
            style={{
              maxHeight: '300px',
              borderBottomLeftRadius: '20px',
              borderBottomRightRadius: '20px',
              boxShadow: '0px 5px 10px rgba(0, 0, 0, 0.1)',
              overflow: 'hidden',
              borderTop: '1px dashed rgba(0,0,0,0.12)'
            }}
          >
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              <FastEntryStyleResults
                filteredNewRecommendations={[]}
                autoCompleteSuggestions={inputValue === "" ? autoCompleteSuggestions : autoCompleteSuggestions.slice(0, 7)}
                inputValue={inputValue}
                onStyleSelect={handleStyleSelect}
                showAutoSearchResults={true}
              />
            </div>
          </div>
        </Popper>
      </div>
    </ClickAwayListener>
  );
});

export default FastEntryStyleSearchBar;